// Shared wiki directory components — used by /wiki list page and homepage
// to render the v4 navigation (Getting Started → Reference Database) sourced
// from the official Territoria game info PDF.

import { Link } from "react-router-dom";
import {
  Swords,
  Shield,
  Coins,
  Landmark,
  Compass,
  User,
  Gem,
  Database,
  Factory,
  Map as MapIcon,
  Star,
  Trophy,
  Heart,
  Target,
  Building2,
  Banknote,
  Globe2,
  ScrollText,
  Hammer,
  Package,
  Rocket,
  BookOpen,
  ChevronRight,
} from "lucide-react";

// Categories explicitly hidden from the public wiki listing.
export const HIDDEN_CATEGORIES = new Set(["Archive"]);

// Canonical top-level navigation order. Unknown categories sort alphabetically after.
export const CATEGORY_PRIORITY = [
  "Getting Started",
  "Character & Progression",
  "Economy & Industry",
  "Politics & Government",
  "Warfare & Military",
  "Items & Equipment",
  "Reference Database",
];

// Section header icon + accent colour per category.
export const CATEGORY_META = {
  "Getting Started":         { icon: Compass,  accent: "#79e6ff" },
  "Character & Progression": { icon: User,     accent: "#3aa0ff" },
  "Economy & Industry":      { icon: Factory,  accent: "#f3c552" },
  "Politics & Government":   { icon: Landmark, accent: "#d978ff" },
  "Warfare & Military":      { icon: Swords,   accent: "#ff5a4a" },
  "Items & Equipment":       { icon: Shield,   accent: "#c8d4e3" },
  "Reference Database":      { icon: Database, accent: "#39FF14" },
};

export const VISIBLE_CATEGORY_ORDER = CATEGORY_PRIORITY;

// Per-entry icon + accent + eyebrow. Default fallback used for unknown slugs.
export const ENTRY_META = {
  // Getting Started
  overview:                  { icon: Compass,    accent: "#79e6ff", eyebrow: "Start Here" },
  "account-creation":        { icon: User,       accent: "#3aa0ff", eyebrow: "Step 1" },
  "interface":               { icon: MapIcon,    accent: "#79e6ff", eyebrow: "UI Tour" },
  // Character & Progression
  "resource-pools":          { icon: Heart,      accent: "#ff5a4a", eyebrow: "Pools" },
  "skills-progression":      { icon: Star,       accent: "#f3c552", eyebrow: "Skills & XP" },
  "perks-specializations":   { icon: Trophy,     accent: "#d978ff", eyebrow: "Career Trees" },
  "recovery-environment":    { icon: Heart,      accent: "#39FF14", eyebrow: "Recovery" },
  // Economy & Industry
  "regional-resources":      { icon: Gem,        accent: "#39FF14", eyebrow: "Resources" },
  "infrastructure-taxes":    { icon: Building2,  accent: "#f3c552", eyebrow: "State Econ" },
  "markets-currency":        { icon: Coins,      accent: "#f3c552", eyebrow: "Markets" },
  "production-supply-chains":{ icon: Hammer,     accent: "#f3c552", eyebrow: "Industry" },
  "terracoin":               { icon: Banknote,   accent: "#39FF14", eyebrow: "Crypto" },
  // Politics & Government
  "sovereign-states":        { icon: Landmark,   accent: "#d978ff", eyebrow: "States" },
  "parliament-laws":         { icon: ScrollText, accent: "#d978ff", eyebrow: "Legislation" },
  "forms-of-government":     { icon: Landmark,   accent: "#79e6ff", eyebrow: "Regimes" },
  "diplomacy-coalitions":    { icon: Globe2,     accent: "#79e6ff", eyebrow: "Diplomacy" },
  // Warfare & Military
  "military-equipment":      { icon: Shield,     accent: "#c8d4e3", eyebrow: "Logistics" },
  "warfare-operations":      { icon: Swords,     accent: "#ff5a4a", eyebrow: "Operations" },
  "combat-resolution":       { icon: Target,     accent: "#ff5a4a", eyebrow: "Combat" },
  "resistance-recovery":     { icon: Rocket,     accent: "#39FF14", eyebrow: "Liberation" },
  // Items & Equipment
  weapons:                   { icon: Swords,     accent: "#ff5a4a", eyebrow: "Weapons" },
  armor:                     { icon: Shield,     accent: "#c8d4e3", eyebrow: "Armor" },
  ammunition:                { icon: Package,    accent: "#f3c552", eyebrow: "Ammo" },
  // Reference Database
  "db-resources":            { icon: Gem,        accent: "#39FF14", eyebrow: "Reference" },
  "db-processed-materials":  { icon: Package,    accent: "#39FF14", eyebrow: "Reference" },
  "db-production-chains":    { icon: Hammer,     accent: "#39FF14", eyebrow: "Reference" },
  "db-combat-formulas":      { icon: Target,     accent: "#39FF14", eyebrow: "Reference" },
  "db-government-positions": { icon: BookOpen,   accent: "#39FF14", eyebrow: "Reference" },
};

export const DEFAULT_ENTRY_META = { icon: ChevronRight, accent: "#3aa0ff", eyebrow: "Entry" };

// Group + sort pages into ordered [category, entries] tuples.
// Anything in HIDDEN_CATEGORIES is excluded so admins can soft-hide content
// by moving it to "Archive" without deleting.
export function groupVisibleSections(pages) {
  const map = new Map();
  for (const p of pages) {
    if (HIDDEN_CATEGORIES.has(p.category)) continue;
    if (!map.has(p.category)) map.set(p.category, []);
    map.get(p.category).push(p);
  }
  for (const items of map.values()) {
    items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }
  const known = CATEGORY_PRIORITY.filter(c => map.has(c));
  const extra = [...map.keys()].filter(c => !CATEGORY_PRIORITY.includes(c)).sort();
  return [...known, ...extra].map(c => [c, map.get(c)]);
}

// Hex → rgba helper
export function hex(c, a) {
  const h = (c || "#3aa0ff").replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

// Single category tree section
export function CategoryTree({ category, entries }) {
  const catMeta = CATEGORY_META[category];
  const CatIcon = catMeta?.icon;
  const catAccent = catMeta?.accent || "#3aa0ff";
  return (
    <section
      className="panel overflow-hidden"
      data-testid={`category-section-${category.replace(/\s+/g, "-").toLowerCase()}`}
    >
      {/* Section header */}
      <div
        className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[var(--t-border)]"
        style={{
          background: `linear-gradient(90deg, ${hex(catAccent, 0.10)} 0%, transparent 70%)`,
          borderBottomColor: hex(catAccent, 0.25),
        }}
      >
        <div className="flex items-center gap-3 min-w-0">
          {CatIcon && (
            <span
              className="w-8 h-8 md:w-9 md:h-9 inline-flex items-center justify-center border bg-[#050912] flex-shrink-0"
              style={{
                borderColor: catAccent,
                boxShadow: `0 0 12px ${hex(catAccent, 0.35)}, inset 0 0 10px ${hex(catAccent, 0.1)}`,
              }}
            >
              <CatIcon className="w-4 h-4 md:w-4.5 md:h-4.5" style={{ color: catAccent }} strokeWidth={1.7} />
            </span>
          )}
          <h2
            className="font-display text-lg md:text-2xl uppercase tracking-[0.04em] leading-tight truncate"
            style={{ color: "var(--t-text)" }}
          >
            {category}
          </h2>
        </div>
        <span
          className="font-mono text-[10px] md:text-xs tracking-[0.22em] uppercase whitespace-nowrap"
          style={{ color: catAccent }}
        >
          {entries.length} {entries.length === 1 ? "entry" : "entries"}
        </span>
      </div>

      {/* Entries list */}
      <ul className="divide-y divide-[var(--t-border)]">
        {entries.map((p, idx) => {
          const meta = ENTRY_META[p.slug] || DEFAULT_ENTRY_META;
          const Icon = meta.icon;
          const isLast = idx === entries.length - 1;
          return (
            <li key={p.slug}>
              <Link
                to={`/wiki/${p.slug}`}
                className="group flex items-center gap-3 md:gap-4 px-4 md:px-5 py-3.5 md:py-4 hover:bg-[rgba(58,160,255,0.04)] transition"
                data-testid={`page-card-${p.slug}`}
              >
                {/* Tree connector — hidden on small viewports */}
                <span
                  aria-hidden
                  className="hidden md:inline-flex flex-shrink-0 w-6 font-mono text-[var(--t-text-muted)] text-sm select-none"
                >
                  {isLast ? "└─" : "├─"}
                </span>

                {/* Icon */}
                <div
                  className="w-10 h-10 md:w-11 md:h-11 flex-shrink-0 flex items-center justify-center border bg-[#050912] transition group-hover:scale-105"
                  style={{
                    borderColor: meta.accent,
                    boxShadow: `inset 0 0 14px ${hex(meta.accent, 0.10)}, 0 0 14px ${hex(meta.accent, 0.18)}`,
                  }}
                >
                  <Icon className="w-5 h-5" style={{ color: meta.accent }} strokeWidth={1.6} />
                </div>

                {/* Title + summary */}
                <div className="min-w-0 flex-1">
                  <span
                    className="font-mono text-[9px] md:text-[10px] tracking-[0.22em] uppercase block"
                    style={{ color: meta.accent }}
                  >
                    {meta.eyebrow}
                  </span>
                  <h3
                    className="font-display text-base md:text-lg uppercase leading-tight truncate"
                    style={{ color: "var(--t-text)" }}
                  >
                    {p.title}
                  </h3>
                  <p className="text-xs md:text-sm text-[var(--t-text-dim)] mt-1 line-clamp-1 md:line-clamp-2">
                    {p.summary}
                  </p>
                </div>

                {/* CTA */}
                <div
                  className="flex-shrink-0 flex items-center gap-1.5 text-[10px] font-mono tracking-[0.2em] uppercase opacity-70 group-hover:opacity-100 transition"
                  style={{ color: meta.accent }}
                >
                  <span className="hidden md:inline">Read</span>
                  <ChevronRight className="w-4 h-4 transition group-hover:translate-x-0.5" />
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
